import { useQueries } from '@tanstack/react-query';
import { getCompanyListApi, CompanyListV1Response } from './company';

// `company/all?c=<id>` returns the FULL multilevel descendant set as a flat
// list (see buildCompanyTree in pages/CompanyDetails/Tabs/companyTree.ts,
// WHEEL-372), so one request per selected company resolves every level.
const DESCENDANTS_PAGE_SIZE = 500;

export const companyDescendantsQueryKey = (companyId: number) =>
  ['company', 'descendants', companyId] as const;

export const fetchCompanyDescendantIds = async (
  companyId: number,
): Promise<number[]> => {
  const ids: number[] = [];
  let page = 1;
  for (;;) {
    const response = await getCompanyListApi({
      d: 'n',
      u: 'n',
      s: 'y',
      h: '',
      ipp: DESCENDANTS_PAGE_SIZE,
      pn: page,
      c: companyId,
    });
    const data: CompanyListV1Response = response?.data ?? {
      Total: 0,
      Companies: [],
    };
    const companies = data.Companies ?? [];
    companies.forEach(company => {
      // The response includes the queried company itself — drop it, the
      // caller already has it.
      if (company.Id !== companyId) ids.push(company.Id);
    });
    if (companies.length === 0 || page * DESCENDANTS_PAGE_SIZE >= data.Total) {
      break;
    }
    page += 1;
  }
  return ids;
};

export interface CompanyDescendantsResult {
  descendantsByCompanyId: Record<number, number[]>;
  /** True while any selected company's descendant list has not resolved yet
   * (uses `isPending` so a query that has not started fetching still counts —
   * required to gate the dashboard's first fetch on deep links). */
  isResolvingDescendants: boolean;
}

export const useCompanyDescendantIds = (
  companyIds: number[],
  enabled: boolean,
): CompanyDescendantsResult => {
  const results = useQueries({
    queries: companyIds.map(companyId => ({
      queryKey: companyDescendantsQueryKey(companyId),
      queryFn: () => fetchCompanyDescendantIds(companyId),
      enabled,
      staleTime: 5 * 60 * 1000,
    })),
  });

  const descendantsByCompanyId: Record<number, number[]> = {};
  companyIds.forEach((companyId, index) => {
    descendantsByCompanyId[companyId] = results[index]?.data ?? [];
  });

  return {
    descendantsByCompanyId,
    isResolvingDescendants:
      enabled && results.some(result => result.isPending && !result.isError),
  };
};

/** Union of the selected ids and their resolved descendants, deduped and
 * numerically sorted so the applied params stay byte-identical across
 * renders (Home's applyListParams compares JSON). */
export const expandWithDescendants = (
  selectedIds: number[],
  descendantsByCompanyId: Record<number, number[]>,
): number[] => {
  const expanded = new Set<number>(selectedIds);
  selectedIds.forEach(id => {
    (descendantsByCompanyId[id] ?? []).forEach(descendantId =>
      expanded.add(descendantId),
    );
  });
  return Array.from(expanded).sort((a, b) => a - b);
};
